import ReactDOM from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './lib/query'
// 导入全局样式
import './styles/globals.css'
import './index.css'
import './styles/modal-theme.css'
import App from './App'
import './i18n' // 导入 i18n 配置

// 🆕 SSO 启动性能优化 (Optimistic Boot)
import { performOptimisticRestore } from './stores/authStore';
import { getUserManager } from './features/auth/utils/oidcConfig';

// 1. 立即尝试从本地 Token 乐观恢复用户状态 (首帧渲染)
performOptimisticRestore();

// 2. 提前开始 OIDC 元数据预取 (后台并行)
if (import.meta.env.VITE_SKIP_SSO !== 'true') {
  getUserManager();
}

const enableDebugEnv = import.meta.env.VITE_ENABLE_SSO_DEBUG === 'true';

// ==================== 移动端调试工具 ====================
// 仅在 URL ?debug=true 或显式开启 VITE_ENABLE_SSO_DEBUG 时启用
const shouldEnableDebug = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const debugParam = urlParams.get('debug') === 'true';

  return debugParam || enableDebugEnv;
};

if (shouldEnableDebug()) {
  import('vconsole').then((VConsoleModule) => {
    const VConsole = VConsoleModule.default;
    new VConsole({ theme: 'dark' });

    // 🔥 日志收集数组：存储所有日志用于复制
    const logHistory: Array<{ time: string; type: string; content: string }> = [];

    // 拦截console方法,收集日志
    const originalLog = console.log;
    const originalError = console.error;
    const originalWarn = console.warn;
    const originalInfo = console.info;

    const collectLog = (type: string, args: any[]) => {
      const timestamp = new Date().toLocaleTimeString('zh-CN', { hour12: false });
      const content = args.map(arg => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg, null, 2);
          } catch (e) {
            return String(arg);
          }
        }
        return String(arg);
      }).join(' ');

      logHistory.push({ time: timestamp, type, content });
    };

    console.log = (...args: any[]) => {
      collectLog('LOG', args);
      originalLog.apply(console, args);
    };

    console.error = (...args: any[]) => {
      collectLog('ERROR', args);
      originalError.apply(console, args);
    };

    console.warn = (...args: any[]) => {
      collectLog('WARN', args);
      originalWarn.apply(console, args);
    };

    console.info = (...args: any[]) => {
      collectLog('INFO', args);
      originalInfo.apply(console, args);
    };

    // 🔥 添加全局日志复制函数
    (window as any).__Vinka_COPY_LOGS__ = () => {
      if (logHistory.length === 0) {
        alert('📋 没有日志可复制');
        return;
      }

      const logText = logHistory.map(log =>
        `[${log.time}] ${log.type}: ${log.content}`
      ).join('\n\n');

      // 尝试使用Clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(logText)
          .then(() => {
            alert(`✅ 已复制 ${logHistory.length} 条日志到剪贴板`);
          })
          .catch((err) => {
            console.error('复制失败:', err);
            // 降级方案：显示文本让用户手动复制
            fallbackCopy(logText);
          });
      } else {
        // 旧浏览器降级方案
        fallbackCopy(logText);
      }
    };

    // 降级复制方案：创建临时textarea
    const fallbackCopy = (text: string) => {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();

      try {
        const successful = document.execCommand('copy');
        if (successful) {
          alert(`✅ 已复制 ${logHistory.length} 条日志到剪贴板`);
        } else {
          alert('❌ 复制失败，请手动截图保存日志');
        }
      } catch (err) {
        console.error('降级复制也失败:', err);
        alert('❌ 复制失败，请手动截图保存日志');
      } finally {
        document.body.removeChild(textarea);
      }
    };

    console.log('%c🔍 VConsole 已启用', 'background: #222; color: #bada55; font-size: 14px; padding: 4px;');
    console.log('📱 移动端调试面板已激活');
    console.log('🌍 当前环境:', import.meta.env.MODE);
    console.log('🔗 当前URL:', window.location.href);
    console.log('%c📋 复制日志功能已启用', 'background: #10b981; color: white; font-size: 14px; padding: 4px;');
    console.log('使用方式: window.__Vinka_COPY_LOGS__()');
    console.log('或在VConsole中执行: __Vinka_COPY_LOGS__()');
  });
}

// ==================== 全局调试函数 ====================
// 在手机浏览器控制台或VConsole中可直接调用
import { forceCleanAndReload, debugAuthStorage, cleanAllAuthStorage } from './features/auth/utils/storageUtils';

// 导出到window对象，方便调试
if (typeof window !== 'undefined') {
  (window as any).__Vinka_DEBUG__ = {
    forceCleanAndReload,    // 清除缓存并刷新: window.__Vinka_DEBUG__.forceCleanAndReload()
    debugAuthStorage,       // 查看认证存储: window.__Vinka_DEBUG__.debugAuthStorage()
    cleanAllAuthStorage,    // 仅清除不刷新: window.__Vinka_DEBUG__.cleanAllAuthStorage()
    copyLogs: () => {       // 复制日志: window.__Vinka_DEBUG__.copyLogs()
      if ((window as any).__Vinka_COPY_LOGS__) {
        (window as any).__Vinka_COPY_LOGS__();
      } else {
        alert('⚠️ VConsole未启用，无法复制日志\n请在master环境或添加?debug=true访问');
      }
    }
  };

  console.log('%c🛠️ 调试函数已导出', 'background: #0891b2; color: white; padding: 4px 8px;');
  console.log('使用方式: window.__Vinka_DEBUG__.forceCleanAndReload()');
  console.log('         window.__Vinka_DEBUG__.debugAuthStorage()');
  console.log('         window.__Vinka_DEBUG__.cleanAllAuthStorage()');
  console.log('         window.__Vinka_DEBUG__.copyLogs()');
}
// ========================================================

ReactDOM.createRoot(document.getElementById('root')!).render(
  // 移除StrictMode，避免开发环境下的双重渲染和副作用
  <QueryClientProvider client={queryClient}>
    <App />
  </QueryClientProvider>
) 
